/**
 * Enhanced Greenfield FHIR to VirtualPAT Data Mapper
 *
 * This service provides advanced mapping of FHIR resources to VirtualPAT's
 * surgery-specific data model by looking for surgery information in various
 * FHIR resources.
 */

import { format, parseISO } from 'date-fns';

/**
 * Extract surgery information from FHIR Procedure resources
 */
function extractSurgeryFromProcedures(procedures = []) {
  if (!Array.isArray(procedures) || procedures.length === 0) return {};

  // Look for scheduled or planned surgical procedures
  const surgicalProcedures = procedures.filter(proc => {
    const { status } = proc;
    const category = proc.category?.coding?.[0]?.code;

    // Check if it's a surgical procedure
    const isSurgical =
      category === 'surgical' ||
      proc.code?.text?.toLowerCase().includes('surgery') ||
      proc.code?.coding?.some(c => c.display?.toLowerCase().includes('surgery'));

    // Check if it's scheduled or planned
    const isScheduled = status === 'preparation' || status === 'not-done' || status === 'on-hold';

    return isSurgical && isScheduled;
  });

  if (surgicalProcedures.length > 0) {
    const procedure = surgicalProcedures[0]; // Take the first scheduled surgery

    return {
      surgeryType: procedure.code?.text || procedure.code?.coding?.[0]?.display || '',
      surgeryDate: procedure.performedPeriod?.start || procedure.scheduledPeriod?.start || '',
      surgeonId: procedure.performer?.[0]?.actor?.reference?.split('/')[1] || '',
      surgeonName: procedure.performer?.[0]?.actor?.display || '',
    };
  }

  return {};
}

/**
 * Extract ASA score from Observations
 */
function extractASAFromObservations(observations = []) {
  if (!Array.isArray(observations)) return '';

  // Look for ASA Physical Status observations
  const asaObservation = observations.find(obs => {
    const code = obs.code?.coding?.[0]?.code;
    const system = obs.code?.coding?.[0]?.system;
    const text = obs.code?.text?.toLowerCase() || '';

    return (
      code === '273398008' || // SNOMED code for ASA score
      system?.includes('asa') ||
      text.includes('asa') ||
      text.includes('physical status')
    );
  });

  if (asaObservation) {
    // Extract ASA score from value
    const value =
      asaObservation.valueCodeableConcept?.coding?.[0]?.code ||
      asaObservation.valueCodeableConcept?.text ||
      asaObservation.valueString ||
      '';

    // Extract numeric ASA score (1-6)
    const asaMatch = value.match(/[1-6]/);
    return asaMatch ? asaMatch[0] : value;
  }

  return '';
}

/**
 * Extract surgery information from Appointments
 */
function extractSurgeryFromAppointments(appointments = []) {
  if (!Array.isArray(appointments) || appointments.length === 0) return {};

  // Look for surgical appointments
  const surgicalAppointments = appointments.filter(apt => {
    const serviceType = apt.serviceType?.[0]?.coding?.[0]?.display?.toLowerCase() || '';
    const description = apt.description?.toLowerCase() || '';

    return serviceType.includes('surgery') || description.includes('surgery');
  });

  if (surgicalAppointments.length > 0) {
    const appointment = surgicalAppointments[0];

    return {
      surgeryDate: appointment.start || '',
      surgeryType:
        appointment.serviceType?.[0]?.text ||
        appointment.serviceType?.[0]?.coding?.[0]?.display ||
        appointment.description ||
        '',
      surgeonId:
        appointment.participant
          ?.find(
            p =>
              p.type?.[0]?.coding?.[0]?.code === 'PPRF' || // Primary performer
              p.type?.[0]?.coding?.[0]?.code === 'PRF' // Performer
          )
          ?.actor?.reference?.split('/')[1] || '',
    };
  }

  return {};
}

/**
 * Look for surgery data in Encounters
 */
function extractSurgeryFromEncounters(encounters = []) {
  if (!Array.isArray(encounters) || encounters.length === 0) return {};

  // Look for pre-operative encounters
  const preopEncounters = encounters.filter(enc => {
    const type = enc.type?.[0]?.text?.toLowerCase() || '';
    const reasonCode = enc.reasonCode?.[0]?.text?.toLowerCase() || '';

    return (
      type.includes('pre-op') ||
      type.includes('preoperative') ||
      reasonCode.includes('surgery') ||
      enc.class?.code === 'PRENC'
    ); // Pre-admission encounter
  });

  if (preopEncounters.length > 0) {
    const encounter = preopEncounters[0];

    return {
      surgeryDate: encounter.period?.start || '',
      surgeryType:
        encounter.reasonCode?.[0]?.text || encounter.reasonCode?.[0]?.coding?.[0]?.display || '',
      surgeonId:
        encounter.participant
          ?.find(
            p => p.type?.[0]?.coding?.[0]?.code === 'ATND' // Attending
          )
          ?.individual?.reference?.split('/')[1] || '',
    };
  }

  return {};
}

/**
 * Main enhanced mapping function
 */
export function mapFhirPatientToVirtualPAT(fhirPatient, relatedResources = {}) {
  // Extract basic patient demographics
  const basicInfo = {
    id: fhirPatient.id,
    firstName: fhirPatient.name?.[0]?.given?.[0] || '',
    lastName: fhirPatient.name?.[0]?.family || '',
    dob: fhirPatient.birthDate || '',
    gender: fhirPatient.gender || '',
    mrn: fhirPatient.identifier?.find(id => id.type?.coding?.[0]?.code === 'MR')?.value || '',
    fhirId: fhirPatient.id,
  };

  // Try to extract surgery information from various sources
  let surgeryInfo = {};

  // 1. Check Procedures
  if (relatedResources.procedures?.entry) {
    const procedures = relatedResources.procedures.entry.map(e => e.resource);
    surgeryInfo = { ...surgeryInfo, ...extractSurgeryFromProcedures(procedures) };
  }

  // 2. Check Appointments
  if (relatedResources.appointments?.entry) {
    const appointments = relatedResources.appointments.entry.map(e => e.resource);
    const appointmentInfo = extractSurgeryFromAppointments(appointments);
    surgeryInfo = { ...surgeryInfo, ...appointmentInfo };
  }

  // 3. Check Encounters
  if (relatedResources.encounters?.entry) {
    const encounters = relatedResources.encounters.entry.map(e => e.resource);
    const encounterInfo = extractSurgeryFromEncounters(encounters);
    surgeryInfo = { ...surgeryInfo, ...encounterInfo };
  }

  // Extract ASA score from Observations
  let asaScore = '';
  if (relatedResources.observations?.entry) {
    const observations = relatedResources.observations.entry.map(e => e.resource);
    asaScore = extractASAFromObservations(observations);
  }

  // Extract conditions (comorbidities)
  const conditions = [];
  if (relatedResources.conditions?.entry) {
    conditions.push(
      ...relatedResources.conditions.entry.map(e => ({
        id: e.resource.id,
        code: e.resource.code?.coding?.[0]?.code || '',
        display: e.resource.code?.text || e.resource.code?.coding?.[0]?.display || '',
        status: e.resource.clinicalStatus?.coding?.[0]?.code || '',
        onset: e.resource.onsetDateTime || e.resource.onsetPeriod?.start || '',
      }))
    );
  }

  // Extract medications
  const medications = [];
  if (relatedResources.medicationRequests?.entry) {
    medications.push(
      ...relatedResources.medicationRequests.entry.map(e => ({
        id: e.resource.id,
        name:
          e.resource.medicationCodeableConcept?.text ||
          e.resource.medicationCodeableConcept?.coding?.[0]?.display ||
          '',
        status: e.resource.status || '',
        dosage: e.resource.dosageInstruction?.[0]?.text || '',
      }))
    );
  }

  // Extract allergies
  const allergies = [];
  if (relatedResources.allergyIntolerances?.entry) {
    allergies.push(
      ...relatedResources.allergyIntolerances.entry.map(e => ({
        id: e.resource.id,
        substance: e.resource.code?.text || e.resource.code?.coding?.[0]?.display || '',
        criticality: e.resource.criticality || '',
        type: e.resource.type || '',
      }))
    );
  }

  // Combine all data
  return {
    ...basicInfo,
    surgeryType: surgeryInfo.surgeryType || 'Not Scheduled',
    surgeryDate: surgeryInfo.surgeryDate
      ? format(parseISO(surgeryInfo.surgeryDate), 'yyyy-MM-dd')
      : '',
    surgeonId: surgeryInfo.surgeonId || '',
    surgeonName: surgeryInfo.surgeonName || 'Not Assigned',
    asaScore: asaScore || 'Not Assessed',
    conditions,
    medications,
    allergies,
    // Flag to indicate this is Greenfield data with potentially missing surgery info
    dataSource: 'greenfield',
    needsSurgeryInfo: !surgeryInfo.surgeryType || !surgeryInfo.surgeryDate || !asaScore,
  };
}

/**
 * Fetch all related resources for a patient
 */
export async function fetchPatientWithRelatedData(patientId, accessToken) {
  const baseUrl = '/api/greenfield';
  const headers = {
    Authorization: `Bearer ${accessToken}`,
    Accept: 'application/fhir+json',
  };

  try {
    // Fetch all related resources in parallel
    const [
      patient,
      procedures,
      appointments,
      encounters,
      observations,
      conditions,
      medicationRequests,
      allergyIntolerances,
    ] = await Promise.all([
      fetch(`${baseUrl}/patient/${patientId}`, { headers }).then(r => r.json()),
      fetch(`${baseUrl}/Procedure?patient=${patientId}`, { headers })
        .then(r => r.json())
        .catch(() => null),
      fetch(`${baseUrl}/Appointment?patient=${patientId}`, { headers })
        .then(r => r.json())
        .catch(() => null),
      fetch(`${baseUrl}/Encounter?patient=${patientId}`, { headers })
        .then(r => r.json())
        .catch(() => null),
      fetch(`${baseUrl}/Observation?patient=${patientId}`, { headers })
        .then(r => r.json())
        .catch(() => null),
      fetch(`${baseUrl}/Condition?patient=${patientId}`, { headers })
        .then(r => r.json())
        .catch(() => null),
      fetch(`${baseUrl}/MedicationRequest?patient=${patientId}`, { headers })
        .then(r => r.json())
        .catch(() => null),
      fetch(`${baseUrl}/AllergyIntolerance?patient=${patientId}`, { headers })
        .then(r => r.json())
        .catch(() => null),
    ]);

    const relatedResources = {
      procedures,
      appointments,
      encounters,
      observations,
      conditions,
      medicationRequests,
      allergyIntolerances,
    };

    return mapFhirPatientToVirtualPAT(patient, relatedResources);
  } catch (error) {
    console.error('Error fetching patient with related data:', error);
    throw error;
  }
}

/**
 * Create a local surgery record for a Greenfield patient
 */
export function createSurgeryInfoForPatient(patientId, surgeryInfo) {
  // Store surgery info in localStorage for now
  // In production, this would be stored in a backend database
  const surgeryData = {
    patientId,
    surgeryType: surgeryInfo.surgeryType,
    surgeryDate: surgeryInfo.surgeryDate,
    surgeonId: surgeryInfo.surgeonId,
    surgeonName: surgeryInfo.surgeonName,
    asaScore: surgeryInfo.asaScore,
    createdAt: new Date().toISOString(),
    source: 'manual_entry',
  };

  const storageKey = `virtualPAT_surgery_${patientId}`;
  localStorage.setItem(storageKey, JSON.stringify(surgeryData));

  return surgeryData;
}

/**
 * Get locally stored surgery info for a patient
 */
export function getLocalSurgeryInfo(patientId) {
  const storageKey = `virtualPAT_surgery_${patientId}`;
  const data = localStorage.getItem(storageKey);
  return data ? JSON.parse(data) : null;
}

// ============================================================================
// US Core Resource Mappers (Added for HCA/Full FHIR Integration)
// ============================================================================

/**
 * Map FHIR MedicationStatement to VirtualPAT format
 * MedicationStatement captures medications the patient is taking or has taken
 * @param {Object} medicationStatement - FHIR MedicationStatement resource
 * @returns {Object} Normalized medication object
 */
export function mapMedicationStatementToVirtualPAT(medicationStatement) {
  if (!medicationStatement) return null;

  const medicationRef = medicationStatement.medicationCodeableConcept ||
    medicationStatement.medicationReference;

  return {
    id: medicationStatement.id,
    name:
      medicationRef?.coding?.[0]?.display ||
      medicationRef?.text ||
      medicationRef?.display ||
      'Unknown Medication',
    status: medicationStatement.status || 'unknown',
    dosage: medicationStatement.dosage?.[0]?.text || '',
    route: medicationStatement.dosage?.[0]?.route?.coding?.[0]?.display || '',
    frequency: medicationStatement.dosage?.[0]?.timing?.code?.text || '',
    startDate: medicationStatement.effectivePeriod?.start ||
      medicationStatement.effectiveDateTime || '',
    endDate: medicationStatement.effectivePeriod?.end || '',
    source: 'MedicationStatement',
    _fhirData: medicationStatement,
  };
}

/**
 * Map FHIR Coverage to VirtualPAT insurance format
 * @param {Object} coverage - FHIR Coverage resource
 * @returns {Object} Normalized insurance/coverage object
 */
export function mapCoverageToVirtualPAT(coverage) {
  if (!coverage) return null;

  return {
    id: coverage.id,
    status: coverage.status || 'unknown',
    type: coverage.type?.coding?.[0]?.display || coverage.type?.text || 'Unknown',
    policyHolder: coverage.policyHolder?.display || '',
    subscriber: coverage.subscriber?.display || '',
    subscriberId: coverage.subscriberId || '',
    beneficiary: coverage.beneficiary?.display || '',
    relationship: coverage.relationship?.coding?.[0]?.display || '',
    period: {
      start: coverage.period?.start || '',
      end: coverage.period?.end || '',
    },
    payor: coverage.payor?.[0]?.display || '',
    class: coverage.class?.map(c => ({
      type: c.type?.coding?.[0]?.display || c.type?.text || '',
      value: c.value || '',
      name: c.name || '',
    })) || [],
    network: coverage.network || '',
    costToBeneficiary: coverage.costToBeneficiary?.map(c => ({
      type: c.type?.coding?.[0]?.display || '',
      value: c.valueMoney?.value || c.valueQuantity?.value || '',
      currency: c.valueMoney?.currency || '',
    })) || [],
    _fhirData: coverage,
  };
}

/**
 * Map FHIR Practitioner to VirtualPAT provider format
 * @param {Object} practitioner - FHIR Practitioner resource
 * @returns {Object} Normalized practitioner/provider object
 */
export function mapPractitionerToVirtualPAT(practitioner) {
  if (!practitioner) return null;

  const name = practitioner.name?.[0] || {};
  const fullName = name.text ||
    [name.prefix?.[0], name.given?.[0], name.family, name.suffix?.[0]]
      .filter(Boolean)
      .join(' ');

  return {
    id: practitioner.id,
    name: fullName,
    firstName: name.given?.[0] || '',
    lastName: name.family || '',
    prefix: name.prefix?.[0] || '',
    suffix: name.suffix?.[0] || '',
    gender: practitioner.gender || '',
    npi: practitioner.identifier?.find(i =>
      i.system?.includes('npi') || i.type?.coding?.[0]?.code === 'NPI'
    )?.value || '',
    specialty: practitioner.qualification?.[0]?.code?.coding?.[0]?.display || '',
    telecom: practitioner.telecom?.map(t => ({
      system: t.system || '',
      value: t.value || '',
      use: t.use || '',
    })) || [],
    address: practitioner.address?.[0] ? {
      line: practitioner.address[0].line?.join(', ') || '',
      city: practitioner.address[0].city || '',
      state: practitioner.address[0].state || '',
      postalCode: practitioner.address[0].postalCode || '',
      country: practitioner.address[0].country || '',
    } : null,
    _fhirData: practitioner,
  };
}

/**
 * Map FHIR Organization to VirtualPAT format
 * @param {Object} organization - FHIR Organization resource
 * @returns {Object} Normalized organization object
 */
export function mapOrganizationToVirtualPAT(organization) {
  if (!organization) return null;

  return {
    id: organization.id,
    name: organization.name || 'Unknown Organization',
    type: organization.type?.map(t =>
      t.coding?.[0]?.display || t.text
    ).filter(Boolean).join(', ') || '',
    active: organization.active !== false,
    npi: organization.identifier?.find(i =>
      i.system?.includes('npi') || i.type?.coding?.[0]?.code === 'NPI'
    )?.value || '',
    telecom: organization.telecom?.map(t => ({
      system: t.system || '',
      value: t.value || '',
      use: t.use || '',
    })) || [],
    address: organization.address?.[0] ? {
      line: organization.address[0].line?.join(', ') || '',
      city: organization.address[0].city || '',
      state: organization.address[0].state || '',
      postalCode: organization.address[0].postalCode || '',
      country: organization.address[0].country || '',
    } : null,
    partOf: organization.partOf?.display || '',
    _fhirData: organization,
  };
}

/**
 * Map FHIR Location to VirtualPAT format
 * @param {Object} location - FHIR Location resource
 * @returns {Object} Normalized location object
 */
export function mapLocationToVirtualPAT(location) {
  if (!location) return null;

  return {
    id: location.id,
    name: location.name || 'Unknown Location',
    status: location.status || 'unknown',
    description: location.description || '',
    mode: location.mode || '',
    type: location.type?.map(t =>
      t.coding?.[0]?.display || t.text
    ).filter(Boolean).join(', ') || '',
    telecom: location.telecom?.map(t => ({
      system: t.system || '',
      value: t.value || '',
      use: t.use || '',
    })) || [],
    address: location.address ? {
      line: location.address.line?.join(', ') || '',
      city: location.address.city || '',
      state: location.address.state || '',
      postalCode: location.address.postalCode || '',
      country: location.address.country || '',
    } : null,
    position: location.position ? {
      latitude: location.position.latitude,
      longitude: location.position.longitude,
      altitude: location.position.altitude,
    } : null,
    managingOrganization: location.managingOrganization?.display || '',
    partOf: location.partOf?.display || '',
    _fhirData: location,
  };
}

/**
 * Map an array of FHIR resources using the appropriate mapper
 * @param {Array} resources - Array of FHIR resources
 * @param {string} resourceType - FHIR resource type
 * @returns {Array} Array of normalized resources
 */
export function mapResourceArray(resources, resourceType) {
  if (!Array.isArray(resources)) return [];

  const mappers = {
    MedicationStatement: mapMedicationStatementToVirtualPAT,
    Coverage: mapCoverageToVirtualPAT,
    Practitioner: mapPractitionerToVirtualPAT,
    Organization: mapOrganizationToVirtualPAT,
    Location: mapLocationToVirtualPAT,
  };

  const mapper = mappers[resourceType];
  if (!mapper) {
    console.warn(`[greenfieldEnhancedMapper] No mapper for resource type: ${resourceType}`);
    return resources;
  }

  return resources.map(mapper).filter(Boolean);
}
